import { useState } from 'react';
import { FileText, ChevronDown, Loader2 } from 'lucide-react';
import { useMessageTemplates } from '@/hooks/useMessageTemplates';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface MessageTemplatePickerProps {
  channel?: string;
  onSelect: (body: string) => void;
  /** Optional context for resolving placeholders like [LINK], [KUNDE], [SAG] */
  context?: {
    project_id?: string | null;
    customer_id?: string | null;
    customer_name?: string | null;
    project_title?: string | null;
  };
}

/**
 * Resolves placeholder tokens in template body.
 * - [LINK] / [link] / [BOOKING] → fresh booking/portal link via generate-portal-link edge function
 * - [KUNDE] / [kunde] → customer name
 * - [SAG] / [sag] → project title
 */
async function resolveTemplate(body: string, ctx: MessageTemplatePickerProps['context']): Promise<string> {
  let out = body;

  if (ctx?.customer_name) {
    out = out.replace(/\[(KUNDE|kunde|Kunde)\]/g, ctx.customer_name);
  }
  if (ctx?.project_title) {
    out = out.replace(/\[(SAG|sag|Sag)\]/g, ctx.project_title);
  }

  const linkRegex = /\[(LINK|link|Link|BOOKING|booking|Booking)\]/g;
  if (linkRegex.test(out) && ctx?.project_id) {
    try {
      const { data, error } = await supabase.functions.invoke('generate-portal-link', {
        body: { project_id: ctx.project_id, customer_id: ctx.customer_id },
      });
      const url: string | undefined = data?.url || data?.portal_url || data?.link;
      if (!error && url) {
        out = out.replace(linkRegex, url);
      } else {
        toast.error('Kunne ikke generere booking-link — placeholder bevaret');
      }
    } catch (err) {
      console.error('Failed to resolve [LINK]:', err);
      toast.error('Kunne ikke generere booking-link');
    }
  }

  return out;
}

export function MessageTemplatePicker({ channel, onSelect, context }: MessageTemplatePickerProps) {
  const [open, setOpen] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const { data: templates } = useMessageTemplates(channel);

  if (!templates || templates.length === 0) return null;

  const handlePick = async (id: string, body: string) => {
    setResolvingId(id);
    try {
      const resolved = await resolveTemplate(body, context);
      onSelect(resolved);
      setOpen(false);
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
          title="Vælg skabelon"
        >
          <FileText className="w-3 h-3" />
          Skabelon
          <ChevronDown className="w-2.5 h-2.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="w-64 p-1.5 max-h-56 overflow-y-auto">
        <div className="space-y-0.5">
          {templates.map(t => (
            <button
              key={t.id}
              disabled={resolvingId !== null}
              onClick={() => handlePick(t.id, t.body)}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted/60 transition-colors group disabled:opacity-50"
            >
              <p className="text-xs font-medium text-foreground flex items-center gap-1.5">
                {resolvingId === t.id && <Loader2 className="w-3 h-3 animate-spin" />}
                {t.title}
              </p>
              <p className="text-[10px] text-muted-foreground truncate mt-0.5 group-hover:text-foreground/70">{t.body}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
